import { useState } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Instagram, Facebook, Send, CheckCircle, Clock } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      setIsSubmitted(true);
      toast.success('Message sent successfully!');
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* Hero */}
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden">
        <img
          src="/assets/images/vicenzo-pool.jpg"
          alt="Contact Us"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/40" />
        <div className="relative z-10 text-center text-white">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-sm tracking-[0.3em] uppercase mb-4"
          >
            Get In Touch
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-5xl md:text-7xl font-serif"
          >
            Contact Us
          </motion.h1>
        </div>
      </section>

      {/* Contact Content */}
      <section className="section-padding bg-bone">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0 }}
              transition={{ duration: 0.7 }}
            >
              <p className="text-sm tracking-[0.3em] uppercase text-bronze mb-4">Let's Talk</p>
              <h2 className="text-4xl md:text-5xl font-serif mb-6">
                Start Your Design Journey
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-10">
                Ready to transform your space? We'd love to hear from you. Fill out the form or reach out directly—we're here to help bring your vision to life.
              </p>

              <div className="space-y-6 mb-10">
                <a
                  href="tel:+19413281233"
                  data-testid="contact-phone"
                  className="flex items-center gap-4 text-foreground hover:text-bronze transition-colors group"
                >
                  <div className="w-12 h-12 flex items-center justify-center border border-border group-hover:border-bronze transition-colors">
                    <Phone size={20} />
                  </div>
                  <div>
                    <p className="text-xs tracking-widest uppercase text-muted-foreground mb-1">Phone</p>
                    <p className="font-serif">(941) 328-1233</p>
                  </div>
                </a>

                <a
                  href="mailto:info@cnpurestyle.com"
                  data-testid="contact-email"
                  className="flex items-center gap-4 text-foreground hover:text-bronze transition-colors group"
                >
                  <div className="w-12 h-12 flex items-center justify-center border border-border group-hover:border-bronze transition-colors">
                    <Mail size={20} />
                  </div>
                  <div>
                    <p className="text-xs tracking-widest uppercase text-muted-foreground mb-1">Email</p>
                    <p className="font-serif">info@cnpurestyle.com</p>
                  </div>
                </a>

                <div className="flex items-center gap-4 group">
                  <div className="w-12 h-12 flex items-center justify-center border border-border">
                    <MapPin size={20} />
                  </div>
                  <div>
                    <p className="text-xs tracking-widest uppercase text-muted-foreground mb-1">Address</p>
                    <p className="font-serif">8131 Lakewood Main St #207</p>
                    <p className="font-serif">Lakewood Ranch, FL 34202</p>
                  </div>
                </div>

                <div className="flex items-center gap-4 group">
                  <div className="w-12 h-12 flex items-center justify-center border border-border">
                    <Clock size={20} />
                  </div>
                  <div>
                    <p className="text-xs tracking-widest uppercase text-muted-foreground mb-1">Hours</p>
                    <p className="font-serif">Monday - Friday: 8am - 4:30pm</p>
                    <p className="font-serif">Saturday - Sunday: Closed</p>
                  </div>
                </div>
              </div>

              {/* Social Links */}
              <div>
                <p className="text-xs tracking-widest uppercase text-muted-foreground mb-4">Follow Us</p>
                <div className="flex gap-4">
                  <a
                    href="https://www.instagram.com/cnpurestyle/"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-instagram"
                    className="w-12 h-12 flex items-center justify-center border border-border text-foreground hover:border-bronze hover:text-bronze transition-all duration-300"
                  >
                    <Instagram size={20} />
                  </a>
                  <a
                    href="https://www.facebook.com/cnpurestyle/"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-facebook"
                    className="w-12 h-12 flex items-center justify-center border border-border text-foreground hover:border-bronze hover:text-bronze transition-all duration-300"
                  >
                    <Facebook size={20} />
                  </a>
                </div>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0 }}
              transition={{ duration: 0.7 }}
              className="bg-white p-8 md:p-12 border border-border"
            >
              {isSubmitted ? (
                <div className="text-center py-12">
                  <CheckCircle size={48} className="mx-auto text-bronze mb-6" />
                  <h3 className="text-2xl font-serif mb-4">Thank You!</h3>
                  <p className="text-muted-foreground mb-8">
                    We've received your message and will be in touch shortly.
                  </p>
                  <button
                    onClick={() => setIsSubmitted(false)}
                    data-testid="send-another-btn"
                    className="btn-outline"
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-testid="contact-form">
                  <h3 className="text-2xl font-serif mb-8">Send Us a Message</h3>
                  
                  <div className="space-y-6">
                    <div>
                      <Label htmlFor="name" className="text-xs tracking-widest uppercase text-muted-foreground">
                        Name *
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        data-testid="input-name"
                        className="mt-2 rounded-none border-0 border-b border-border focus:border-bronze bg-transparent px-0"
                        placeholder="Your Name"
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="email" className="text-xs tracking-widest uppercase text-muted-foreground">
                        Email *
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        data-testid="input-email"
                        className="mt-2 rounded-none border-0 border-b border-border focus:border-bronze bg-transparent px-0"
                        placeholder="your@email.com"
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="phone" className="text-xs tracking-widest uppercase text-muted-foreground">
                        Phone
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        data-testid="input-phone"
                        className="mt-2 rounded-none border-0 border-b border-border focus:border-bronze bg-transparent px-0"
                        placeholder="(XXX) XXX-XXXX"
                      />
                    </div>

                    <div>
                      <Label htmlFor="message" className="text-xs tracking-widest uppercase text-muted-foreground">
                        Message *
                      </Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        data-testid="input-message"
                        className="mt-2 rounded-none border-0 border-b border-border focus:border-bronze bg-transparent px-0 min-h-[120px] resize-none"
                        placeholder="Tell us about your project..."
                        required
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      data-testid="submit-btn"
                      className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? 'Sending...' : 'Send Message'}
                      <Send size={16} />
                    </button>
                  </div>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="h-[500px] relative">
        <iframe
          title="Charlene Neal Pure Style Location"
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3540.0891927394285!2d-82.4208661!3d27.3989444!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x88c340adc00e7531%3A0x8b7e38ce7c8d7c8a!2s8131%20Lakewood%20Main%20St%20%23207%2C%20Lakewood%20Ranch%2C%20FL%2034202!5e0!3m2!1sen!2sus!4v1700000000000!5m2!1sen!2sus"
          width="100%"
          height="100%"
          style={{ border: 0 }}
          allowFullScreen=""
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          className="grayscale hover:grayscale-0 transition-all duration-500"
          data-testid="google-map"
        />
        <div className="absolute bottom-6 left-6 bg-white p-6 shadow-lg max-w-sm hidden md:block">
          <p className="font-serif text-lg mb-2">Visit Our Showroom</p>
          <p className="text-sm text-muted-foreground">
            8131 Lakewood Main St #207<br />
            Lakewood Ranch, FL 34202
          </p>
          <a 
            href="https://maps.google.com/?q=8131+Lakewood+Main+St+%23207,+Lakewood+Ranch,+FL+34202"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-bronze hover:underline mt-2 inline-block"
          >
            Get Directions →
          </a>
        </div>
      </section>

      {/* Part of Neal Communities */}
      <section className="py-12 bg-muted">
        <div className="container-custom text-center">
          <p className="text-xs tracking-[0.3em] uppercase text-muted-foreground mb-2">
            Part of the
          </p>
          <p className="font-serif text-xl text-foreground">
            Neal Family of Companies
          </p>
        </div>
      </section>
    </div>
  );
};
